/**
 * Central Game Type Registry
 *
 * THE single source of truth for all supported game types.
 * Add a new game type here and every app picks it up automatically.
 */

import { type SportCode, SUPPORTED_SPORT_CODES } from './leagues';

/** Union of all supported game type codes */
export type GameTypeCode = 'pickem' | 'squares' | 'daily_reveal' | 'squads';

export interface GameTypeConfig {
  /** Lowercase game type identifier stored in DB, e.g. 'pickem' */
  code: GameTypeCode;
  /** Human-readable label, e.g. "Pick'em" */
  label: string;
  /** Admin panel route prefix, e.g. '/dashboard/pickems' */
  adminRoute: string;
  /** Icon name used in admin sidebar (from Icon component) */
  iconName: string;
  /** If set, these sports are excluded from this game type */
  excludedSports?: SportCode[];
}

export const GAME_TYPES: readonly GameTypeConfig[] = [
  { code: 'pickem', label: "Pick'em", adminRoute: '/dashboard/pickems', iconName: 'CrownSimple' },
  { code: 'squares', label: 'Squares', adminRoute: '/dashboard/squares', iconName: 'GridFour', excludedSports: ['PGA'] },
  { code: 'daily_reveal', label: 'Daily Reveal', adminRoute: '/dashboard/daily-reveal', iconName: 'Lightning', excludedSports: ['PGA'] },
  { code: 'squads', label: 'Squads', adminRoute: '/dashboard/squads', iconName: 'Users' },
];

/** e.g. ['pickem', 'squares', 'daily_reveal'] */
export const GAME_TYPE_CODES: readonly GameTypeCode[] = GAME_TYPES.map(g => g.code);

/** e.g. { pickem: "Pick'em", squares: "Squares", daily_reveal: "Daily Reveal" } */
export const GAME_TYPE_LABELS: Record<GameTypeCode, string> = Object.fromEntries(
  GAME_TYPES.map(g => [g.code, g.label])
) as Record<GameTypeCode, string>;

/** Get the human-readable label for a game type code */
export function getGameTypeLabel(code: string): string {
  return GAME_TYPE_LABELS[code as GameTypeCode] ?? code;
}

/** Runtime validation helper */
export function isValidGameType(code: string): code is GameTypeCode {
  return (GAME_TYPE_CODES as readonly string[]).includes(code);
}

/** Get sports available for a game type */
export function getSportsForGameType(gameTypeCode: GameTypeCode): SportCode[] {
  const gameType = GAME_TYPES.find(g => g.code === gameTypeCode);
  if (!gameType) return [];
  if (gameType.excludedSports) {
    return (SUPPORTED_SPORT_CODES as unknown as SportCode[]).filter(
      s => !gameType.excludedSports!.includes(s)
    );
  }
  return [...SUPPORTED_SPORT_CODES] as SportCode[];
}

/** Check if a sport is available for a game type */
export function isSportAvailableForGameType(sport: string, gameTypeCode: GameTypeCode): boolean {
  return getSportsForGameType(gameTypeCode).includes(sport.toUpperCase() as SportCode);
}
